// src/pages/modules/OrderSummaryReportPage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchOrderSummaryReport,
  exportOrderSummaryReport,
  clearError,
} from 'store/slices/reportSlice';
import { fetchEmployeeDropdown } from 'store/slices/commonSlice';

import PageHeader from 'components/common/PageHeader';
import { Card, CardHeader, CardTitle, CardContent } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import {
  ShoppingCart,
  IndianRupee,
  CheckCircle,
  XCircle,
  Package,
  Clock,
  PackageCheck,
  Filter,
  X,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import LogDateFilter from 'components/common/LogDateFilter';
import DataTable from 'components/common/DataTable';
import { format } from 'date-fns';

const OrderSummaryReportPage = () => {
  const dispatch = useDispatch();

  const {
    orderSummaryReport = { data: { summary: {}, results: [] }, pagination: null },
    isLoading,
    error,
  } = useSelector((state) => state.report);

  const summary = orderSummaryReport.data.summary || {};
  const results = orderSummaryReport.data.results || [];
  const pagination = orderSummaryReport.pagination || {};

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilters, setDateFilters] = useState({});
  const [isAdvancedFilterExpanded, setIsAdvancedFilterExpanded] = useState(false);

  const [advancedFilters, setAdvancedFilters] = useState({
    status: 'all',
    min_revenue: '',
    max_revenue: '',
  });

  const totalPages = pagination.total_pages || 1;

  const hasActiveAdvancedFilters = Object.values(advancedFilters).some(
    (value) => value !== '' && value !== 'all'
  );

  const refetch = useCallback(() => {
    const apiParams = {
      page,
      page_size: pageSize,
      search: searchTerm,
      report_type: 'order_summary',
      ...dateFilters,
      ...(advancedFilters.status !== 'all' && { status: advancedFilters.status }),
      ...(advancedFilters.min_revenue && { min_revenue: advancedFilters.min_revenue }),
      ...(advancedFilters.max_revenue && { max_revenue: advancedFilters.max_revenue }),
    };

    dispatch(fetchOrderSummaryReport(apiParams));
  }, [dispatch, page, pageSize, searchTerm, dateFilters, advancedFilters]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, pageSize, dateFilters, advancedFilters]);

  // Optional: Load employees if you plan to add employee filter later
  useEffect(() => {
    dispatch(fetchEmployeeDropdown({}));
  }, [dispatch]);

  const handleAdvancedFilterChange = (key, value) => {
    setAdvancedFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const resetAdvancedFilters = () => {
    setAdvancedFilters({
      status: 'all',
      min_revenue: '',
      max_revenue: '',
    });
  };

  const columns = [
    {
      accessorKey: 'period',
      header: 'Date / Period',
      cell: ({ row }) => {
        const date = row.original.period;
        return (
          <span className="font-medium">
            {date ? format(new Date(date), 'dd MMM yyyy') : '-'}
          </span>
        );
      }
    },
    {
      accessorKey: 'total_orders',
      header: 'Total Orders',
      cell: ({ row }) => (
        <Badge variant="outline" className="font-semibold">
          <ShoppingCart className="h-3 w-3 mr-1" />
          {row.original.total_orders}
        </Badge>
      ),
    },
    {
      accessorKey: 'total_revenue',
      header: 'Total Revenue (₹)',
      cell: ({ row }) => (
        <span className="font-semibold text-green-600">
          ₹{(row.original.total_revenue || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      accessorKey: 'avg_order_value',
      header: 'Avg Order Value (₹)',
      cell: ({ row }) => (
        <span className="font-medium">
          ₹{(row.original.avg_order_value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      accessorKey: 'delivered_orders',
      header: 'Delivered',
      cell: ({ row }) => (
        <Badge variant="default" className="bg-green-600">
          <CheckCircle className="h-3 w-3 mr-1" />
          {row.original.delivered_orders}
        </Badge>
      ),
    },
    {
      accessorKey: 'cancelled_orders',
      header: 'Cancelled',
      cell: ({ row }) => (
        <Badge variant="destructive">
          <XCircle className="h-3 w-3 mr-1" />
          {row.original.cancelled_orders}
        </Badge>
      ),
    },
    {
      accessorKey: 'pending_orders',
      header: 'Pending',
      cell: ({ row }) => (
        <Badge variant="secondary" className="bg-amber-600 text-white">
          <Clock className="h-3 w-3 mr-1" />
          {row.original.pending_orders}
        </Badge>
      ),
    },
    {
      accessorKey: 'preparing_orders',
      header: 'Preparing',
      cell: ({ row }) => (
        <Badge variant="secondary" className="bg-blue-600 text-white">
          <Package className="h-3 w-3 mr-1" />
          {row.original.preparing_orders}
        </Badge>
      ),
    },
    {
      accessorKey: 'ready_orders',
      header: 'Ready',
      cell: ({ row }) => (
        <Badge variant="default" className="bg-purple-600">
          <PackageCheck className="h-3 w-3 mr-1" />
          {row.original.ready_orders}
        </Badge>
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Order Summary Report"
        description="Overview of orders, revenue, and delivery performance over time"
      />

      <LogDateFilter onFilter={(f) => setDateFilters(f)} />

      {/* Advanced Filters Card */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Advanced Filters
              {hasActiveAdvancedFilters && (
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  Active
                </span>
              )}
            </CardTitle>
            <div className="flex gap-2">
              {hasActiveAdvancedFilters && (
                <Button variant="outline" size="sm" onClick={resetAdvancedFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Reset
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsAdvancedFilterExpanded(!isAdvancedFilterExpanded)}
              >
                {isAdvancedFilterExpanded ? (
                  <>Hide <ChevronUp className="h-4 w-4 ml-1" /></>
                ) : (
                  <>Show <ChevronDown className="h-4 w-4 ml-1" /></>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        {isAdvancedFilterExpanded && (
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Revenue Range (₹)</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="number"
                    placeholder="Min"
                    value={advancedFilters.min_revenue}
                    onChange={(e) => handleAdvancedFilterChange('min_revenue', e.target.value)}
                  />
                  <Input
                    type="number"
                    placeholder="Max"
                    value={advancedFilters.max_revenue}
                    onChange={(e) => handleAdvancedFilterChange('max_revenue', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Summary Cards - Now includes ALL fields from your API */}
      {/* Summary Cards - All cards equal width, fully responsive */}
      <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
            <ShoppingCart className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.total_orders ?? 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <IndianRupee className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              ₹{(summary.total_revenue || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Order Value</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ₹{(summary.avg_order_value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Delivered</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {summary.delivered_orders ?? 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Cancelled</CardTitle>
            <XCircle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">
              {summary.cancelled_orders ?? 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <Clock className="h-4 w-4 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">
              {summary.pending_orders ?? 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Preparing</CardTitle>
            <Package className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {summary.preparing_orders ?? 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ready</CardTitle>
            <PackageCheck className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">
              {summary.ready_orders ?? 0}
            </div>
          </CardContent>
        </Card>
      </div>

      <DataTable
        data={results}
        totalCount={pagination.count}
        columns={columns}
        searchPlaceholder="Search by date/period..."
        onSearch={setSearchTerm}
        showSearch={false}
        pageIndex={page - 1}
        pageSize={pageSize}
        pageCount={totalPages}
        onPageChange={(p) => setPage(p + 1)}
        onPageSizeChange={(newSize) => setPageSize(newSize)}
        isLoading={isLoading}
        onExport={() =>
          dispatch(exportOrderSummaryReport({
            filters: {
              page,
              page_size: pageSize,
              search: searchTerm,
              report_type: 'order_summary',
              ...dateFilters,
              ...(advancedFilters.status !== 'all' && { status: advancedFilters.status }),
              ...(advancedFilters.min_revenue && { min_revenue: advancedFilters.min_revenue }),
              ...(advancedFilters.max_revenue && { max_revenue: advancedFilters.max_revenue }),
            },
          }))
        }
        showExport={true}
        showImport={false}
      />
    </div>
  );
};

export default OrderSummaryReportPage;